import axios from 'axios';
import API from './axiosInstance';

const API_URL = process.env.REACT_APP_API_URL;

const clientPortalAxios = axios.create({
	baseURL: `${API_URL}/api`,
	withCredentials: true,
});

clientPortalAxios.interceptors.request.use((config) => {
	const token = localStorage.getItem('clientToken');
	if (token) {
		config.headers.Authorization = `Bearer ${token}`;
	}
	const portalHost = window.location.host;
	if (portalHost) {
		config.headers['x-portal-host'] = portalHost;
	}
	return config;
});

clientPortalAxios.interceptors.response.use(
	(response) => response,
	(error) => {
		const status = error.response?.status;
		const msg = String(error.response?.data?.msg || '');
		const isClientAuthFailure =
			status === 401 &&
			(msg.includes('Client authentication') ||
				msg.includes('Invalid client token') ||
				msg.includes('Token is not valid') ||
				msg.includes('Client portal access is not active'));

		if (isClientAuthFailure && typeof localStorage !== 'undefined') {
			localStorage.removeItem('clientToken');
		}
		return Promise.reject(error);
	},
);

export const clientAuthApi = {
	validateInvite: (token) =>
		clientPortalAxios.get('/client-auth/validate-invite', { params: { token } }),
	acceptInvite: (token, password) =>
		clientPortalAxios.post('/client-auth/accept-invite', { token, password }),
	login: (email, password) =>
		clientPortalAxios.post('/client-auth/login', { email, password }),
	logout: () => clientPortalAxios.post('/client-auth/logout'),
};

export const clientPortalApi = {
	getMe: () => clientPortalAxios.get('/client-portal/me'),
	getInvoices: () => clientPortalAxios.get('/client-portal/invoices'),
	getInvoice: (id) => clientPortalAxios.get(`/client-portal/invoices/${id}`),
	requestInvoiceStatusUpdate: (invoiceId, payload) =>
		clientPortalAxios.post(`/client-portal/invoices/${invoiceId}/status-request`, payload),
	getInvoiceStatusRequests: () =>
		clientPortalAxios.get('/client-portal/invoice-status-requests'),
	getNotifications: () => clientPortalAxios.get('/client-portal/notifications'),
	markNotificationRead: (id) =>
		clientPortalAxios.patch(`/client-portal/notifications/${id}/read`),
	markAllNotificationsRead: () =>
		clientPortalAxios.post('/client-portal/notifications/read-all'),
	getTeam: () => clientPortalAxios.get('/client-portal/team'),
	inviteTeamMember: (email) =>
		clientPortalAxios.post('/client-portal/team/invite', { email }),
	getFolders: () => clientPortalAxios.get('/client-portal/folders'),
	getListContacts: (listId) =>
		clientPortalAxios.get(`/client-portal/lists/${listId}/contacts`),
	uploadListContacts: (listId, contacts) =>
		clientPortalAxios.post(`/client-portal/lists/${listId}/contacts`, { contacts }),
	getCampaigns: () => clientPortalAxios.get('/client-portal/campaigns'),
	getCampaign: (id) => clientPortalAxios.get(`/client-portal/campaigns/${id}`),
	getCampaignAnalytics: (id) =>
		clientPortalAxios.get(`/client-portal/campaigns/${id}/analytics`),
	getProjects: () => clientPortalAxios.get('/client-portal/projects'),
	getProject: (id) => clientPortalAxios.get(`/client-portal/projects/${id}`),
	getProjectDetail: (id) => clientPortalAxios.get(`/client-portal/projects/${id}/detail`),
	getTaskDetail: (projectId, taskId, subtaskId) =>
		subtaskId
			? clientPortalAxios.get(
					`/client-portal/projects/${projectId}/tasks/${taskId}/subtasks/${subtaskId}`,
				)
			: clientPortalAxios.get(`/client-portal/projects/${projectId}/tasks/${taskId}`),
	addGeneralInfoAttachment: (projectId, payload) =>
		clientPortalAxios.post(
			`/client-portal/projects/${projectId}/general-info/attachments`,
			payload,
		),
	addTaskFile: (projectId, taskId, payload) =>
		clientPortalAxios.post(
			`/client-portal/projects/${projectId}/tasks/${taskId}/files`,
			payload,
		),
	addSubtaskFile: (projectId, taskId, subtaskId, payload) =>
		clientPortalAxios.post(
			`/client-portal/projects/${projectId}/tasks/${taskId}/subtasks/${subtaskId}/files`,
			payload,
		),
	addProjectComment: (projectId, payload) =>
		clientPortalAxios.post(`/client-portal/projects/${projectId}/comments`, payload),
	addTaskComment: (projectId, taskId, payload) =>
		clientPortalAxios.post(
			`/client-portal/projects/${projectId}/tasks/${taskId}/comments`,
			payload,
		),
	deleteTaskComment: (projectId, taskId, commentId) =>
		clientPortalAxios.delete(
			`/client-portal/projects/${projectId}/tasks/${taskId}/comments/${commentId}`,
		),
	editTaskComment: (projectId, taskId, commentId, payload) =>
		clientPortalAxios.put(
			`/client-portal/projects/${projectId}/tasks/${taskId}/comments/${commentId}`,
			payload,
		),
	addTaskCommentReply: (projectId, taskId, commentId, payload) =>
		clientPortalAxios.post(
			`/client-portal/projects/${projectId}/tasks/${taskId}/comments/${commentId}/replies`,
			payload,
		),
	deleteTaskCommentReply: (projectId, taskId, commentId, replyId) =>
		clientPortalAxios.delete(
			`/client-portal/projects/${projectId}/tasks/${taskId}/comments/${commentId}/replies/${replyId}`,
		),
	editTaskCommentReply: (projectId, taskId, commentId, replyId, payload) =>
		clientPortalAxios.put(
			`/client-portal/projects/${projectId}/tasks/${taskId}/comments/${commentId}/replies/${replyId}`,
			payload,
		),
	toggleTaskCommentReaction: (projectId, taskId, commentId, emoji) =>
		clientPortalAxios.post(
			`/client-portal/projects/${projectId}/tasks/${taskId}/comments/${commentId}/reactions`,
			{ emoji },
		),
	toggleTaskCommentReplyReaction: (projectId, taskId, commentId, replyId, emoji) =>
		clientPortalAxios.post(
			`/client-portal/projects/${projectId}/tasks/${taskId}/comments/${commentId}/replies/${replyId}/reactions`,
			{ emoji },
		),
	addSubtaskComment: (projectId, taskId, subtaskId, payload) =>
		clientPortalAxios.post(
			`/client-portal/projects/${projectId}/tasks/${taskId}/subtasks/${subtaskId}/comments`,
			payload,
		),
	deleteSubtaskComment: (projectId, taskId, subtaskId, commentId) =>
		clientPortalAxios.delete(
			`/client-portal/projects/${projectId}/tasks/${taskId}/subtasks/${subtaskId}/comments/${commentId}`,
		),
	editSubtaskComment: (projectId, taskId, subtaskId, commentId, payload) =>
		clientPortalAxios.put(
			`/client-portal/projects/${projectId}/tasks/${taskId}/subtasks/${subtaskId}/comments/${commentId}`,
			payload,
		),
	addSubtaskCommentReply: (projectId, taskId, subtaskId, commentId, payload) =>
		clientPortalAxios.post(
			`/client-portal/projects/${projectId}/tasks/${taskId}/subtasks/${subtaskId}/comments/${commentId}/replies`,
			payload,
		),
	deleteSubtaskCommentReply: (projectId, taskId, subtaskId, commentId, replyId) =>
		clientPortalAxios.delete(
			`/client-portal/projects/${projectId}/tasks/${taskId}/subtasks/${subtaskId}/comments/${commentId}/replies/${replyId}`,
		),
	editSubtaskCommentReply: (projectId, taskId, subtaskId, commentId, replyId, payload) =>
		clientPortalAxios.put(
			`/client-portal/projects/${projectId}/tasks/${taskId}/subtasks/${subtaskId}/comments/${commentId}/replies/${replyId}`,
			payload,
		),
	toggleSubtaskCommentReaction: (projectId, taskId, subtaskId, commentId, emoji) =>
		clientPortalAxios.post(
			`/client-portal/projects/${projectId}/tasks/${taskId}/subtasks/${subtaskId}/comments/${commentId}/reactions`,
			{ emoji },
		),
	toggleSubtaskCommentReplyReaction: (
		projectId,
		taskId,
		subtaskId,
		commentId,
		replyId,
		emoji,
	) =>
		clientPortalAxios.post(
			`/client-portal/projects/${projectId}/tasks/${taskId}/subtasks/${subtaskId}/comments/${commentId}/replies/${replyId}/reactions`,
			{ emoji },
		),
	getAiPosts: () => clientPortalAxios.get('/client-portal/ai-posts'),
	getAiPost: (id) => clientPortalAxios.get(`/client-portal/ai-posts/${id}`),
	updateAiPost: (id, payload) =>
		clientPortalAxios.patch(`/client-portal/ai-posts/${id}`, payload),
	uploadAiPostMedia: (id, file) => {
		const formData = new FormData();
		formData.append('file', file);
		return clientPortalAxios.post(`/client-portal/ai-posts/${id}/media`, formData);
	},
	reviewAiPost: (id, payload) =>
		clientPortalAxios.post(`/client-portal/ai-posts/${id}/review`, payload),
	getAiPostAuditLog: (id) => clientPortalAxios.get(`/client-portal/ai-posts/${id}/audit-log`),
};

export const clientPortalAdminApi = {
	getSettings: (companyId) =>
		API.get('/client-portal/admin/settings', { params: { companyId } }),
	updateSettings: (companyId, payload) =>
		API.put('/client-portal/admin/settings', { companyId, ...payload }),
	getClientStatus: (companyId, clientId) =>
		API.get(`/client-portal/admin/clients/${clientId}/status`, {
			params: { companyId },
		}),
	updateClientAccess: (companyId, clientId, payload) =>
		API.put(`/client-portal/admin/clients/${clientId}/access`, {
			companyId,
			...payload,
		}),
	inviteClient: (companyId, clientId, email, options = {}) =>
		API.post(`/client-portal/admin/clients/${clientId}/invite`, {
			companyId,
			email,
			...options,
		}),
	revokeClient: (companyId, clientId) =>
		API.post(`/client-portal/admin/clients/${clientId}/revoke`, { companyId }),
	getActivitySummary: (companyId) =>
		API.get('/client-portal/admin/activity/summary', { params: { companyId } }),
	getPendingInvoiceRequests: (companyId) =>
		API.get('/client-portal/admin/activity/invoice-requests', {
			params: { companyId },
		}),
	resolveInvoiceRequest: (companyId, requestId, payload) =>
		API.post(`/client-portal/admin/activity/invoice-requests/${requestId}/resolve`, {
			companyId,
			...payload,
		}),
	revokePortalUser: (companyId, clientId, portalUserId) =>
		API.post('/client-portal/admin/portal-users/revoke', {
			companyId,
			clientId,
			portalUserId,
		}),
	resendPortalUserInvite: (companyId, clientId, portalUserId) =>
		API.post('/client-portal/admin/portal-users/resend-invite', {
			companyId,
			clientId,
			portalUserId,
		}),
	reactivatePortalUser: (companyId, clientId, portalUserId) =>
		API.post('/client-portal/admin/portal-users/reactivate', {
			companyId,
			clientId,
			portalUserId,
		}),
	removePortalUser: (companyId, clientId, portalUserId) =>
		API.post('/client-portal/admin/portal-users/remove', {
			companyId,
			clientId,
			portalUserId,
		}),
};

export default clientPortalAxios;
